/**
 * @spec docs/specs/features/staking-position-ui.md#phase-2-authentication
 * Combined authentication state hook
 */
import { useCallback, useMemo, useState } from 'react';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { isSocialAuth, hasEmbeddedWallet } from '@/lib/auth';

export interface UseAuthReturn {
  /** Whether user is authenticated (via any method) */
  isAuthenticated: boolean;
  /** Whether user has completed social auth (email, Google, passkey) */
  hasSocialAuth: boolean;
  /** Whether user has a Privy embedded wallet */
  hasEmbeddedWallet: boolean;
  /** Whether user only has wallet connection (no social auth) */
  isWalletOnlyMode: boolean;
  /** User's email if logged in via email */
  email: string | null;
  /** User's primary wallet address */
  walletAddress: string | null;
  /** Whether authentication is loading */
  isLoading: boolean;
  /** Open sign-in modal */
  login: () => void;
  /** Log out */
  logout: () => Promise<void>;
  /** Get access token for authenticated requests */
  getAccessToken: () => Promise<string | null>;
  /** Open upgrade prompt (wallet-only → social auth) */
  showUpgradePrompt: boolean;
  setShowUpgradePrompt: (show: boolean) => void;
  /** Check if action requires auth, show prompt if not authenticated */
  requireAuth: () => boolean;
}

/**
 * Hook for managing authentication state and actions
 *
 * Provides unified access to:
 * - Authentication status (social vs wallet-only)
 * - User info (email, wallet address)
 * - Auth actions (login, logout)
 * - Access token for API requests
 * - Upgrade prompt management
 */
export function useAuth(): UseAuthReturn {
  const {
    authenticated,
    ready,
    user,
    login,
    logout: privyLogout,
    getAccessToken: privyGetAccessToken,
  } = usePrivy();
  const { wallets } = useWallets();

  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // Check auth types (memoized to avoid recomputation on unrelated renders)
  const hasSocialAuthStatus = useMemo(() => isSocialAuth(user), [user]);
  const hasEmbeddedWalletStatus = useMemo(() => hasEmbeddedWallet(user), [user]);
  const isWalletOnlyMode = useMemo(
    () => authenticated && !hasSocialAuthStatus,
    [authenticated, hasSocialAuthStatus]
  );

  // Get user email
  const email = useMemo(() => {
    const rawEmail = user?.email?.address ?? null;
    // TEMPORARY: mask email for demo purposes
    return rawEmail === 'lsdan@trebuchet.ltd' ? '********@******.com' : rawEmail;
  }, [user]);

  // Get primary wallet address (prefer embedded wallet)
  const { walletAddress } = useMemo(() => {
    const embedded = wallets.find((w) => w.walletClientType === 'privy');
    const external = wallets.find((w) => w.walletClientType !== 'privy');
    return { walletAddress: embedded?.address ?? external?.address ?? null };
  }, [wallets]);

  // Logout wrapper
  const logout = useCallback(async () => {
    await privyLogout();
  }, [privyLogout]);

  // Get access token
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!authenticated) return null;
    try {
      return await privyGetAccessToken();
    } catch {
      return null;
    }
  }, [authenticated, privyGetAccessToken]);

  // Require auth for transaction - returns true if authenticated, shows prompt if not
  const requireAuth = useCallback((): boolean => {
    if (!authenticated) {
      setShowUpgradePrompt(true);
      return false;
    }
    return true;
  }, [authenticated]);

  return {
    isAuthenticated: authenticated,
    hasSocialAuth: hasSocialAuthStatus,
    hasEmbeddedWallet: hasEmbeddedWalletStatus,
    isWalletOnlyMode,
    email,
    walletAddress,
    isLoading: !ready,
    login,
    logout,
    getAccessToken,
    showUpgradePrompt,
    setShowUpgradePrompt,
    requireAuth,
  };
}
